import {
	Autoplay, KeyboardScope, LogLevel, PerformanceMode, PlayerOptionsInterface
} from '@cbsinteractive/avia-js';
import { OverridesInterface } from '@cbsinteractive/avia-js/dist/types/iface/OverridesInterface';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IPlayerOptionsManager } from 'library/ts/player/adapter/options/IPlayerOptionsManager';
import { blankMP4Base64 } from 'library/ts/utilities/Autoplay';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IImports } from 'tag/ts/tag/interfaces/config/imports/IImports';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { PlayerLoadingTimeout } from 'tag/ts/tag/model/type/PlayerLoadingTimeout';
import { IAviaPluginsManager } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPluginsManager';
import { IReinitializablePluginDiff } from 'tag/ts/tag/player/adapters/avia/interfaces/IReinitializablePluginDiff';
import { AviaPluginsManager } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPluginsManager';
import { STVPlatformConfigs } from 'tag/ts/tag/player/stv/configs/STVPlatformConfigs';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description VTG Avia player configuration orchestrator for Smart Tag integration
 *
 * **Player Configuration Orchestration:**
 * Manages the complete VTG Avia player configuration by coordinating platform-specific
 * settings, plugin configurations, and Smart Tag business logic requirements.
 * Serves as the configuration assembly point for all VTG player options.
 *
 * **Configuration Coordination:**
 * - **Plugin Management**: Coordinates with AviaPluginsManager for plugin configurations
 * - **Platform Optimization**: Applies platform-specific settings (Desktop/Mobile/Smart TV)
 * - **Smart Tag Integration**: Incorporates Smart Tag business logic into VTG configuration
 * - **Performance Tuning**: Optimizes VTG player settings for different deployment environments
 *
 * **VTG Player Options Assembly:**
 * - **Autoplay Configuration**: Handles autoplay policies and detection across platforms
 * - **Performance Settings**: Applies performance mode and logging level configurations
 * - **Plugin Integration**: Assembles all plugin configurations into cohesive player options
 * - **Container Management**: Configures DOM container and keyboard scope settings
 * - **Platform Adaptation**: Adjusts settings for Desktop, Mobile Web, and Smart TV platforms
 *
 * **Business Logic Integration:**
 * - **Content-Specific Configuration**: Adapts player settings based on content type
 * - **User Context**: Applies user-specific settings and subscription tier considerations
 * - **Geographic Optimization**: Handles international vs domestic configuration differences
 * - **Debug Support**: Provides development and testing configuration options
 *
 * **Configuration Flow:**
 * Smart Tag Business Logic → AviaPlayerOptionsManager → VTG Player Options → VTG Player Instance
 *
 * **Business Impact:**
 * Critical for VTG player initialization, ensuring optimal performance and functionality
 * while maintaining Smart Tag's control over player behavior and business logic.
 */
export class AviaPlayerOptionsManager extends AbstractTagClass implements IPlayerOptionsManager {
	// reference to Tag imports
	#tagConfigImports: IImports;
	#pluginManager: IAviaPluginsManager;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerOptionsManager'
			}
		);
		// set reference to Tag imports from Tag config
		this.#tagConfigImports = this.tag?.model?.config?.tag?.Imports?.params;
		this.#pluginManager = new AviaPluginsManager( { tag: argument?.tag } );
	}

	/**
	 * @description Diffs every plugin that declares itself `isReinitializable()` against its
	 * current `shouldBeUsed()` eligibility, reading current tag params — for reinitializing
	 * plugins during a player-instance reuse (e.g. stream-swap, PLBCK-8461, PLBCK-8475)
	 * @returns {IReinitializablePluginDiff}
	 */
	getReinitializablePluginDiff = (): IReinitializablePluginDiff => {
		return this.#pluginManager.getReinitializablePluginDiff();
	};

	/**
	 * @description Returns player options object
	 * @param {HTMLElement} container
	 * @returns {Promise<IPromiseResolveGeneric & {result: PlayerOptionsInterface}>}
	 */
	getPlayerOptions = ( container: HTMLElement ): Promise<IPromiseResolveGeneric & { result: PlayerOptionsInterface }> => {
		return new Promise(
			( resolve, reject ) => {

				let playerOptions: PlayerOptionsInterface = {
					container: container,
					keyboard: {
						scope: KeyboardScope.NONE
					}
				};

				// turn on VTG player debug if it is requested via top url debugPlayer=1 param
				if ( this.runtimeVO.debug.player === true || this.runtimeVO.debug.lowLevel === true ) {
					playerOptions.logLevel = LogLevel.DEBUG;
				}

				// The number of milliseconds before a network request is considered timed out.
				playerOptions.network = {
					timeout: PlayerLoadingTimeout.NETWORK
				};

				// set performance mode to AUTO
				playerOptions.performanceMode = PerformanceMode.AUTO;

				// TODO: we may want to filter per embed type and set performance mode later on
				// switch ( this.params.SYSTEM.embedType ) {
				//	case EmbedType.HTML5:
				//		playerOptions.performanceMode = PerformanceMode.TV;
				//		break;
				// }

				// filter by Smart Tag skin enabled flag
				switch ( this?.params?.SKIN?.enabled ) {
					case true: // skin enabled
						// setup autoplay => ask VTG player to enable autoplay for all use cases.
						playerOptions.autoplay = Autoplay.ATTEMPT_UNMUTED_THEN_MUTED;
						// render Captions natively if default VTG player skin is forced via SmartTag params,
						// or we are on a mobile device (mobile web === default VTG player UI skin)
						playerOptions.renderTextTrackNatively = this.params.SKIN.defaultSkin || this.capabilities.isMobile.any();
						break;
					case false: // skin disabled
						// autoplay setup
						// playerOptions.autoplay = ( this.params.CONTENT.autoplay === true ? Autoplay.ATTEMPT_UNMUTED_THEN_MUTED : Autoplay.NONE );
						playerOptions.autoplay = Autoplay.ATTEMPT_UNMUTED_THEN_MUTED;
						// cc track rendering
						playerOptions.renderTextTrackNatively = ( this.params.SYSTEM.embedType === EmbedType.DESKTOP );
						break;
				}

				// avia-js-ui mode => Smart Tag skin disabled, avia-js-ui is used instead
				if ( this?.params?.SKIN?.defaultSkin === true ) {
					// Respect user's autoplay preference for avia-js-ui mode
					playerOptions.autoplay = this?.params?.CONTENT?.autoplay === true ?
						Autoplay.ATTEMPT_UNMUTED_THEN_MUTED :
						Autoplay.NONE;
				}

				// get site session data - if fails, continue with defaults (session data is not critical)
				this.tag.model.siteUtilities.getSiteSessionData( `${this.name}.getResourceConfig` ).then(
					( sessionData ) => {
						this?.logger?.log( 'getPlayerOptions', 'Site session data loaded successfully' );
					}
				).catch(
					( error ) => {
						this?.logger?.warn( 'Site session data unavailable, proceeding with defaults:', error );
					}
				).finally(
					() => {
						// Continue with plugin setup regardless of session data result
						playerOptions = this.#finalizePlayerOptions( playerOptions );
						resolve( { msg: 'Player options', result: playerOptions } );
					}
				);
			}
		);
	};

	/**
	 * Finalizes player options by applying all required setup steps
	 * @param playerOptions - Base player options to finalize
	 * @returns Fully configured player options
	 */
	#finalizePlayerOptions = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		// Instantiate Plugins
		playerOptions = this.#setupPlugins( playerOptions );

		// setup Moat tracking
		playerOptions = this.#setupMoat( playerOptions );

		// setup Open Measurement
		playerOptions = this.#setupOpenMeasurement( playerOptions );

		// setup player options overrides
		playerOptions = this.#setupPlayerOptionsOverrides( playerOptions );

		playerOptions = this.#setupPlatformSpecificOptions( playerOptions );

		// merge playerOptions from SmartTag parameters if these were passed in by the page
		if (
			!this.utils.Type.isEmpty( this.params?.TAG?.playerOptions )
			&&
			this.utils.Type.isObject( this.params?.TAG?.playerOptions )
		) {
			playerOptions = this.utils.DeepMerge.deepmerge(
				playerOptions,
				this.params?.TAG?.playerOptions
			);
		}

		this?.logger?.log( 'getPlayerOptions', 'Final playerOptions:', playerOptions );

		return playerOptions;
	};

	#setupPlugins = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		playerOptions.plugins = this.#pluginManager.getAllPlugins() || [];

		this?.logger?.log( 'playerOptions.plugins setup complete', playerOptions.plugins );

		return playerOptions;
	};

	/**
	 * @description Sanity check for plugins containers
	 * @param {PlayerOptionsInterface} playerOptions
	 * @returns {PlayerOptionsInterface}
	 */
	#pluginsPlaceholdersCheck = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		// sanity check
		if ( !this.utils.Type.isArray( playerOptions?.plugins ) ) {
			playerOptions.plugins = [];
		}
		return playerOptions;
	};

	/**
	 * @description Setup Moat tracking and return updated player options
	 * @see {@link https://jira.cbsi.com/browse/CBSVIDEO-5317}
	 * @see {@link https://avia.vtg.paramount.tech/avia-js/latest/docs/guides/ad_plugin_config}
	 * @param {PlayerOptionsInterface} playerOptions
	 * @returns {PlayerOptionsInterface}
	 */
	#setupMoat = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		playerOptions.enableMoat = false;
		return playerOptions;
	};

	/**
	 * @description Setup Open Measurement and return updated player options
	 * @see {@link https://jira.cbsi.com/browse/CBSVIDEO-5317}
	 * @see {@link https://avia.vtg.paramount.tech/avia-js/latest/docs/guides/ad_plugin_config}
	 * @param {PlayerOptionsInterface} playerOptions
	 * @returns {PlayerOptionsInterface}
	 */
	#setupOpenMeasurement = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		playerOptions.enableOm = true;
		return playerOptions;
	};

	/**
	 * @description Setup player options override
	 * @param {PlayerOptionsInterface} playerOptions
	 * @returns {PlayerOptionsInterface}
	 */
	#setupPlayerOptionsOverrides = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		// initialize overrides placeholder object if it does not exist
		if ( this.utils.Type.isEmpty( playerOptions?.overrides ) ) {
			playerOptions.overrides = {} as OverridesInterface;
		}

		// IMPORTANT: replace blank mp4 file URL with base64 string so we can save on extra request to cache server
		playerOptions.overrides.blankVideoUrl = blankMP4Base64;

		// stitcher live streams are not pausable — tell avia-js so it renders the correct icon
		// variant. Universal Pluto rule, not platform-specific (desktop Pluto live is also
		// unpausable); this override only affects avia-js-ui's own pausable property (never read
		// by desktop Skin, which hides its play button on live through its own separate
		// mechanism), so it's safe regardless of platform.
		if ( this.hasStitcherParams() && this.isLive() ) {
			playerOptions.overrides.liveStreamPausable = false;
		}

		return playerOptions;
	};

	/**
	 * @description Setup platform specific playerOptions
	 * @param {PlayerOptionsInterface} playerOptions
	 * @returns {PlayerOptionsInterface}
	 */
	#setupPlatformSpecificOptions = ( playerOptions: PlayerOptionsInterface ): PlayerOptionsInterface => {
		if ( this?.params?.SYSTEM?.embedType === EmbedType.HTML5 ) {
			const platformSpecificOptions = new STVPlatformConfigs( { tag: this?.tag } );
			this?.logger?.log( 'setupPlatformSpecificOptions', 'PLATFORM CONFIGS:', platformSpecificOptions?.playerOptions );
			playerOptions = this.utils.DeepMerge.deepmerge(
				playerOptions,
				platformSpecificOptions?.playerOptions
			);
		}
		return playerOptions;
	};

}